import React, { useContext, useState } from 'react';
import { Trans } from '@ui/i18n/trans';
import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { TextField } from '@ui/forms/input-field/text-field/text-field';
import { SearchIcon } from '@ui/icons/material/Search';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addCompanyMemberApi, allocateCompanyMemberStorage, searchFreeMembersApi } from './api';
import { useAuth } from '@common/auth/use-auth';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';
import { DialogFooter } from '@ui/overlays/dialog/dialog-footer';
import { DialogContext } from '@ui/overlays/dialog/dialog-context';

type Unit = "Bytes" | "KB" | "MB" | "GB" | "TB";

export function StorageDialog({ userId, currentStorage }: { userId: number, currentStorage: number }) {
    function convertBytes(bytes: number): { value: number; unit: Unit } {
        const units: Unit[] = ["Bytes", "KB", "MB", "GB", "TB"];
        let index = 0;

        while (bytes >= 1024 && index < units.length - 1) {
            bytes /= 1024;
            index++;
        }

        return {
            value: parseFloat(bytes.toFixed(2)),
            unit: units[index],
        };
    }


    const [inputValue, setInputValue] = useState(`${convertBytes(currentStorage).value}`);
    const [selectedUnit, setSelectedUnit] = useState<Unit>(convertBytes(currentStorage).unit);
    const [convertedValue, setConvertedValue] = useState(0);
    const maxStorage = 108851651149824;
    const queryClient = useQueryClient()
    const {close} = useContext(DialogContext);
    const {user} = useAuth();

    const { mutate, data } = useMutation({
        mutationKey: ['allocateCompaneMemberStorage'],
        mutationFn: () => { return allocateCompanyMemberStorage({ userId, storage: convertedValue, companyId: user?.id || -1}) },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companyMembers'] });
            queryClient.invalidateQueries({ queryKey: ['storage-summary'] });
            toast(message("Storage allocated successfully!"));
        },
        onError: (error)=>{
            toast.danger(message("No Available space to allocate."));
        }
    })
    

    const handleConversion = () => {
        const conversionRates: Record<Unit, number> = {
            Bytes: 1,
            KB: 1024,
            MB: 1024 * 1024,
            GB: 1024 * 1024 * 1024,
            TB: 1024 * 1024 * 1024 * 1024,
        };

        const bytesValue = (Number(inputValue) || 0) * conversionRates[selectedUnit];
        setConvertedValue(bytesValue);
        if (convertedValue < maxStorage) {
            close();
            mutate();
        }
    };

    return (
        <Dialog>
            <DialogHeader>
                <Trans message="Allocate storage to member" />
            </DialogHeader>
            <DialogBody>
                <div className='flex justify-between'>
                    <div className='pr-10 flex-grow'>
                        <input
                            type="number"
                            className='border p-8 rounded-md w-full'
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                    </div>
                    <select
                        id="unit"
                        value={selectedUnit}
                        onChange={(e) => setSelectedUnit(e.target.value as Unit)}
                        className="bg-gray-50 border py-5 w-[70px] border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    >
                        <option value="Bytes">Bytes</option>
                        <option value="KB">KB</option>
                        <option value="MB">MB</option>
                        <option value="GB">GB</option>
                        <option value="TB">TB</option>
                    </select>
                </div>
            </DialogBody>
            <DialogFooter>
                <button onClick={handleConversion} className='text-white bg-primary px-16 py-6 rounded-md'><Trans message='Done'/></button>
            </DialogFooter>
        </Dialog>
    );
}